/**
 * DocumentRequestList - TASK-374: CO Review System
 *
 * Displays a list of document requests for a given client.
 * Shows status, document type, urgency, and deadline with color-coded badges.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Clock,
  Link2,
  Upload,
  CheckCircle,
  XCircle,
  Loader2,
  Inbox,
  Calendar,
} from 'lucide-react';
import { logger } from '../../../utils/logger';
import { getDocumentRequests } from '../../../services/documentRequestService';
import { WidgetErrorState } from '../../ui/WidgetErrorState';
import { formatDate } from '../../../utils/formatters';
import type { DocumentRequest, DocumentRequestStatus, DocumentRequestUrgency } from '../../../types/complianceWorkflow';

interface DocumentRequestListProps {
  clientId: string;
}

const STATUS_CONFIG: Record<
  DocumentRequestStatus,
  { icon: React.ElementType; bg: string; text: string; label: string }
> = {
  pending: { icon: Clock, bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
  link_sent: { icon: Link2, bg: 'bg-blue-100', text: 'text-blue-700', label: 'Link Sent' },
  uploaded: { icon: Upload, bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Uploaded' },
  fulfilled: { icon: CheckCircle, bg: 'bg-green-100', text: 'text-green-700', label: 'Fulfilled' },
  expired: { icon: XCircle, bg: 'bg-red-100', text: 'text-red-700', label: 'Expired' },
  cancelled: { icon: XCircle, bg: 'bg-gray-100', text: 'text-gray-500', label: 'Cancelled' },
};

const URGENCY_CONFIG: Record<DocumentRequestUrgency, { bg: string; text: string }> = {
  low: { bg: 'bg-gray-100', text: 'text-gray-600' },
  normal: { bg: 'bg-blue-100', text: 'text-blue-600' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700' },
  critical: { bg: 'bg-red-100', text: 'text-red-700' },
};

const formatDocumentType = (type: string): string => {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const DocumentRequestList: React.FC<DocumentRequestListProps> = ({ clientId }) => {
  const [requests, setRequests] = useState<DocumentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDocumentRequests(clientId);
      // TASK-652: validate the collection at the API edge — a non-array body
      // (error envelope, HTML, null) used to crash the whole widget with
      // "requests.map is not a function". Malformed ≠ empty: surface it.
      if (!Array.isArray(data)) {
        throw new Error('Unexpected response while loading document requests — try again');
      }
      setRequests(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load document requests';
      setError(message);
      logger.error('Failed to fetch document requests:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin mb-2" />
        <p className="text-xs text-gray-500">Loading document requests...</p>
      </div>
    );
  }

  if (error) {
    return (
      <WidgetErrorState title="Couldn't load document requests" detail={error} onRetry={fetchRequests} />
    );
  }

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-400">
        <Inbox className="w-8 h-8 mb-2" />
        <p className="text-sm">No document requests</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <FileText className="w-4 h-4" />
        Document Requests ({requests.length})
      </h4>

      <div className="space-y-2">
        {requests.map((request) => {
          const statusCfg = STATUS_CONFIG[request.status];
          const urgencyCfg = URGENCY_CONFIG[request.urgency];
          const StatusIcon = statusCfg.icon;

          const isOverdue =
            request.deadline &&
            request.status !== 'fulfilled' &&
            request.status !== 'cancelled' &&
            new Date(request.deadline) < new Date();

          return (
            <div
              key={request.id}
              className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 flex-1 min-w-0">
                  <StatusIcon className={`w-4 h-4 mt-0.5 shrink-0 ${statusCfg.text}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {formatDocumentType(request.document_type)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {request.reason}
                    </p>

                    <div className="flex items-center flex-wrap gap-2 mt-2">
                      {/* Status badge */}
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`}
                      >
                        {statusCfg.label}
                      </span>

                      {/* Urgency badge */}
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${urgencyCfg.bg} ${urgencyCfg.text}`}
                      >
                        {request.urgency.charAt(0).toUpperCase() + request.urgency.slice(1)}
                      </span>

                      {/* Deadline */}
                      {request.deadline && (
                        <span
                          className={`inline-flex items-center gap-1 text-xs ${
                            isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'
                          }`}
                        >
                          <Calendar className="w-3 h-3" />
                          {formatDate(request.deadline)}
                          {isOverdue && ' (overdue)'}
                        </span>
                      )}
                    </div>

                    {/* Metadata row */}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                      <span>Requested: {formatDate(request.created_at)}</span>
                      {request.requester_name && (
                        <span>by {request.requester_name}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentRequestList;
